import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useState } from 'react'
import { Alert, Form, InputGroup } from 'react-bootstrap'
import Select from '~/components/select'

// Options
const platformsOptions = () => [
  { value: 'windows', label: t`Windows` },
  { value: 'linux', label: t`Linux` },
]

const authTypesOptions = (platform) =>
  (platform === 'windows' && [
    { value: 'ssm', label: t`SSM` },
    { value: 'winrm', label: t`WinRM` },
  ]) || [
    { value: 'ssm', label: t`SSM` },
    { value: 'ssh', label: t`SSH` },
  ]

const authMethodsOptions = () => [
  { value: 'basic', label: t`Basic` },
  { value: 'certificate', label: t`Certificate` },
]

// Inputs
export const NameInput = ({ name, handleNameChange, errors }) => {
  return <RequiredField fieldId='name' label={t`Name`} value={name} onChange={handleNameChange} error={errors.name} />
}

// Selects
export const PlatformSelect = ({ platform, handlePlatformChange, error }) => {
  return (
    <Form.Group className='mb-3'>
      <Form.Label htmlFor='platformSelect'>
        <Trans>Platform</Trans>
      </Form.Label>
      <Select
        id='platformSelect'
        onChange={handlePlatformChange}
        options={platformsOptions()}
        selected={platform}
        required
        error={error}
      />
      {error && (
        <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}

export const AuthTypeSelect = ({ authType, platform, handleAuthTypeChange, error }) => {
  return (
    <Form.Group className='mb-3'>
      <Form.Label htmlFor='authTypeSelect'>
        <Trans>Auth Type</Trans>
      </Form.Label>
      <Select
        id='authTypeSelect'
        onChange={handleAuthTypeChange}
        options={authTypesOptions(platform)}
        selected={authType}
        required
        error={error}
      />
      {error && (
        <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}

const WindowsWinRMSelect = (authMethod, handleAuthMethodChange, errors) => {
  return (
    <Form.Group className='mb-3'>
      <Form.Label htmlFor='authMethodSelect'>
        <Trans>Auth Method</Trans>
      </Form.Label>
      <Select
        id='authMethodSelect'
        onChange={handleAuthMethodChange}
        options={authMethodsOptions()}
        selected={authMethod}
        required
        error={errors.authMethod}
      />
      {errors.authMethod && (
        <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
          {errors.authMethod}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}

// Auth fields
const SSMFields = (authFields, handleAuthFieldsChange, errors) => {
  return (
    <>
      <RequiredField
        fieldId='role'
        label={t`Role`}
        value={authFields.role}
        onChange={handleAuthFieldsChange}
        error={errors.role}
      />
      <RequiredField
        fieldId='region'
        label={t`Region`}
        value={authFields.region}
        onChange={handleAuthFieldsChange}
        error={errors.region}
      />
      <RequiredField
        fieldId='s3Bucket'
        label={t`S3 Bucket Name`}
        value={authFields.s3Bucket}
        onChange={handleAuthFieldsChange}
        error={errors.s3Bucket}
      />
    </>
  )
}

const SSHFields = (authFields, handleAuthFieldsChange, errors) => {
  return (
    <>
      <RequiredField
        fieldId='username'
        label={t`Username`}
        value={authFields.username}
        onChange={handleAuthFieldsChange}
        error={errors.username}
      />
      <RequiredField
        fieldId='privateKey'
        label={t`Private Key`}
        value={authFields.privateKey}
        onChange={handleAuthFieldsChange}
        error={errors.privateKey}
      />
    </>
  )
}

const WindowsWinRMFields = (authMethod, authFields, handleAuthFieldsChange, errors) => {
  if (authMethod === 'basic') {
    return (
      <>
        <RequiredField
          fieldId='username'
          label={t`Username`}
          value={authFields.username}
          onChange={handleAuthFieldsChange}
          error={errors.username}
        />
        <RequiredField
          fieldId='password'
          label={t`Password`}
          value={authFields.password}
          onChange={handleAuthFieldsChange}
          error={errors.password}
        />
      </>
    )
  } else if (authMethod === 'certificate') {
    return (
      <>
        <RequiredField
          fieldId='username'
          label={t`Username`}
          value={authFields.username}
          onChange={handleAuthFieldsChange}
          error={errors.username}
        />
        <RequiredField
          fieldId='privateKey'
          label={t`Private Key`}
          value={authFields.privateKey}
          onChange={handleAuthFieldsChange}
          error={errors.privateKey}
        />
      </>
    )
  }
}

const WindowsWinRMContainer = (authMethod, authFields, handleAuthMethodChange, handleAuthFieldsChange, errors) => {
  return (
    <>
      {WindowsWinRMSelect(authMethod, handleAuthMethodChange, errors)}
      {WindowsWinRMFields(authMethod, authFields, handleAuthFieldsChange, errors)}
    </>
  )
}

export const DynamicFields = ({
  authType,
  authMethod,
  authFields,
  handleAuthFieldsChange,
  handleAuthMethodChange,
  errors,
}) => {
  switch (authType) {
    case 'ssm':
      return SSMFields(authFields, handleAuthFieldsChange, errors)
    case 'winrm':
      return WindowsWinRMContainer(authMethod, authFields, handleAuthMethodChange, handleAuthFieldsChange, errors)
    case 'ssh':
      return SSHFields(authFields, handleAuthFieldsChange, errors)
  }
}

// Is Default Checkbox
export const DefaultCheckbox = ({ isDefault, setIsDefault }) => {
  return (
    <Form.Group controlId='isDefault' className='mb-3'>
      <Form.Check
        type='checkbox'
        label={t`Is Default?`}
        checked={isDefault}
        onChange={(e) => setIsDefault(e.target.checked)}
      />
    </Form.Group>
  )
}

// Generic components
const RequiredField = ({ fieldId, label, type = 'text', value, onChange, error }) => {
  const [touched, setTouched] = useState(false)
  const [localError, setLocalError] = useState('')

  const handleBlur = (e) => {
    setTouched(true)
    const inputValue = e.target.value.trim()

    if (!inputValue) {
      setLocalError(t`${label} is required`)
    } else {
      setLocalError('')
    }
  }

  const displayError = touched ? localError : error

  return (
    <Form.Group className='mb-3'>
      <Form.Label htmlFor={fieldId}>{label}</Form.Label>
      <InputGroup hasValidation>
        <Form.Control
          required
          id={fieldId}
          type={type}
          value={value || ''}
          onChange={onChange}
          onBlur={handleBlur}
          isInvalid={!!displayError}
        />
        <Form.Control.Feedback type='invalid'>{displayError}</Form.Control.Feedback>
      </InputGroup>
    </Form.Group>
  )
}

export const SubmitErrors = (errors) => {
  if (Object.keys(errors).length > 0) {
    return (
      <div>
        <Alert variant='danger' dismissible>
          {Object.entries(errors).map(([key, value]) => (
            <p key={key}>
              {snakeToTitle(key)}: {value}
            </p>
          ))}
        </Alert>
      </div>
    )
  }
}

function snakeToTitle(snakeStr) {
  return snakeStr
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}
